import CloseIcon from "@mui/icons-material/Close"
import {
  AppBar,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Dialog as MuiDialog,
  DialogProps as MuiDialogProps,
  Slide,
  Toolbar,
  Typography,
} from "@mui/material"
import { TransitionProps } from "@mui/material/transitions"
import { ReactNode, forwardRef } from "react"

const Transition = forwardRef(function Transition(
  props: TransitionProps & {
    children: React.ReactElement
  },
  ref: React.Ref<unknown>,
) {
  return <Slide direction="up" ref={ref} {...props} />
})

/**
 * Props for the Dialog component.
 * Extends MUI DialogProps, excluding children to enforce custom structure.
 */
export type DialogProps = Omit<MuiDialogProps, "children"> & {
  /** Content to be displayed in the dialog body */
  children: ReactNode
  /** Optional component to be rendered in the dialog actions section */
  actionsComponent?: ReactNode
  /** Title text displayed in the dialog header */
  title: string
  /** Callback function triggered when dialog is closed */
  onClose: () => void
  /** Whether clicking the backdrop should close the dialog. Defaults to false */
  closeOnBackdropClick?: boolean
  /** Whether to show the close button. Defaults to true **/
  showCloseButton?: boolean
}

/**
 * A fullscreen dialog component with slide-up transition and app bar header.
 *
 * Features:
 * - App bar with title and close button
 * - Configurable backdrop click behavior
 * - Optional actions section at bottom
 * - Slide-up transition animation
 *
 * @example
 * ```tsx
 * <Dialog
 *   open={isOpen}
 *   title="Edit User"
 *   onClose={handleClose}
 *   actionsComponent={<Button onClick={handleSave}>Save</Button>}
 *   closeOnBackdropClick,
 * >
 *   <TextField label="Name" />
 * </Dialog>
 * ```
 */
const Dialog = ({
  children,
  actionsComponent,
  title,
  closeOnBackdropClick = false,
  showCloseButton = true,
  onClose,
  ...props
}: DialogProps) => {
  return (
    <MuiDialog
      TransitionComponent={Transition}
      onClose={(_e, reason) => {
        if (!closeOnBackdropClick && reason === "backdropClick") return
        onClose()
      }}
      {...props}
    >
      <DialogTitle sx={{ p: 0 }}>
        <AppBar sx={{ position: "relative" }}>
          <Toolbar sx={{ display: "flex" }}>
            {showCloseButton && (
              <IconButton edge="start" color="inherit" onClick={onClose} aria-label="close dialog">
                <CloseIcon />
              </IconButton>
            )}
            <Typography variant="h6" component="div">
              {title}
            </Typography>
          </Toolbar>
        </AppBar>
      </DialogTitle>
      <DialogContent sx={{ mt: "20px" }}>{children}</DialogContent>
      <DialogActions sx={{ px: 3, py: 1 }}>{actionsComponent}</DialogActions>
    </MuiDialog>
  )
}

export default Dialog
